import { useIsFetching, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { type BaseCourseApi, type CourseStatusApi } from "../../models/api";
import { getCourseProgressById } from "../../services";
import { getActiveCourse, setActiveCourse } from "../slices/activeCourse";
import {
  CourseSlice,
  LessonSlice,
  ModuleSlice,
  WorkoutSlice,
  getCourses,
  getLessons,
  getModules,
  getWorkouts,
  setCourse,
  setLessons,
  setModules,
  setWorkouts,
} from "../slices/data";

const ENDPOINT_URL = "/api/assessments/learningpath/course/$courseId";

const selectCourseProgress = (id?: BaseCourseApi["id"]) =>
  createSelector(
    getActiveCourse,
    getCourses,
    getModules,
    getLessons,
    getWorkouts,
    (
      activeCourseId,
      allCourses,
      allModules,
      allLessons,
      allWorkouts
    ): CourseStatusApi | null => {
      const courseId = id || activeCourseId;

      if (!courseId) return null;

      const course = allCourses[courseId];

      if (!course || !("modules" in course)) return null;

      let isValid = true;

      const modules = [];
      let lessons = [];
      let workouts = [];

      for (const moduleId of course.modules) {
        const module = allModules[moduleId];
        if (!module || !("lessons" in module)) {
          isValid = false;
          break;
        }
        for (const lessonId of module.lessons) {
          const lesson = allLessons[lessonId];
          if (!lesson || !("workouts" in lesson)) {
            isValid = false;
            break;
          }
          for (const workoutId of lesson.workouts) {
            const workout = allWorkouts[workoutId];
            if (!workout) {
              isValid = false;
              break;
            }
            if (!isValid) break;
            workouts.push(workout);
          }
          if (!isValid) break;
          lessons.push({ ...lesson, workouts });
          workouts = [];
        }
        if (!isValid) break;
        modules.push({ ...module, lessons });
        lessons = [];
      }

      if (!isValid) return null;

      return {
        ...course,
        modules,
      } as CourseStatusApi;
    }
  );

const onSuccess =
  (data: CourseStatusApi, params?: BaseCourseApi["id"]) =>
  async (dispatch, getState) => {
    const state = getState();

    const moduleIds: ModuleSlice["id"][] = [];

    const modules: ModuleSlice[] = [];
    const lessons: LessonSlice[] = [];
    const workouts: WorkoutSlice[] = [];

    for (const module of data.modules) {
      moduleIds.push(module.id);
      modules.push({
        ...module,
        lessons: module.lessons.map(({ id }) => id),
      } as ModuleSlice);
      for (const lesson of module.lessons) {
        lessons.push({
          ...lesson,
          workouts: lesson.workouts.map(({ id }) => id),
        } as LessonSlice);
        workouts.push(...lesson.workouts);
      }
    }

    const course = { ...data, modules: moduleIds } as CourseSlice;

    batch(() => {
      if (!state.activeCourse && !params) {
        dispatch(setActiveCourse(course.id));
      }
      dispatch(setCourse(course));
      dispatch(setModules(modules));
      dispatch(setLessons(lessons));
      dispatch(setWorkouts(workouts));
    });
  };

export const useCourseProgress = (id?: BaseCourseApi["id"] | string) => {
  const parsedId = Number.parseInt(id?.toString(), 10);
  const isValidId = !id || (Number.isNaN(parsedId) ? false : true);
  const queryKey = [ENDPOINT_URL, parsedId];
  const dispatch = useDispatch();
  const dataInStore = useSelector(selectCourseProgress(parsedId));
  const isAlreadyFetching = useIsFetching({ queryKey });
  const shouldRefetch = !dataInStore && !isAlreadyFetching && isValidId;

  const { data, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => getCourseProgressById(parsedId),
    enabled: shouldRefetch,
    onSuccess: (data) => dispatch(onSuccess(data, parsedId)),
  });

  const courseProgress = dataInStore || data;

  useEffect(() => {
    if (shouldRefetch || !courseProgress) {
      refetch();
    }
  }, [refetch, shouldRefetch, courseProgress]);

  return {
    data: isValidId ? courseProgress || null : null,
    isFetching,
  };
};
